import { createContext, useContext, useEffect, useState } from 'react'

const SidebarContext = createContext()

export const useSidebar = () => useContext(SidebarContext)

export const SIDEBAR_COLLAPSED_WIDTH = 73
export const SIDEBAR_EXPANDED_WIDTH = 240
export const SIDEBAR_DRAWER_BREAKPOINT = 1280

export const SidebarProvider = ({ children }) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const [isBelowDrawerBreakpoint, setIsBelowDrawerBreakpoint] = useState(
		() =>
			typeof window !== 'undefined' &&
			window.innerWidth < SIDEBAR_DRAWER_BREAKPOINT
	)
	const [viewMode, setViewModeState] = useState(() =>
		localStorage.getItem('loadfetcher-view-mode') === 'focused'
			? 'focused'
			: 'standard'
	)
	const [sidebarIconMode, setSidebarIconModeState] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-sidebar-icon-mode')
		if (saved === 'show' || saved === 'hide') return saved
		return localStorage.getItem('loadfetcher-view-mode') === 'focused'
			? 'hide'
			: 'show'
	})

	useEffect(() => {
		const onResize = () =>
			setIsBelowDrawerBreakpoint(window.innerWidth < SIDEBAR_DRAWER_BREAKPOINT)
		window.addEventListener('resize', onResize)
		return () => window.removeEventListener('resize', onResize)
	}, [])

	const isDrawerMode = isBelowDrawerBreakpoint || sidebarIconMode === 'hide'

	const toggleSidebar = () => setIsExpanded(prev => !prev)
	const expandSidebar = () => setIsExpanded(true)
	const collapseSidebar = () => setIsExpanded(false)

	const setViewMode = mode => {
		setViewModeState(mode)
		localStorage.setItem('loadfetcher-view-mode', mode)
	}

	const setSidebarIconMode = mode => {
		setSidebarIconModeState(mode)
		localStorage.setItem('loadfetcher-sidebar-icon-mode', mode)
		setIsExpanded(false)
	}

	// In drawer mode the sidebar is off-canvas, so it doesn't reserve width.
	const sidebarWidth = isDrawerMode
		? 0
		: isExpanded
			? SIDEBAR_EXPANDED_WIDTH
			: SIDEBAR_COLLAPSED_WIDTH

	return (
		<SidebarContext.Provider
			value={{
				isExpanded,
				isMobile: isBelowDrawerBreakpoint,
				isDrawerMode,
				toggleSidebar,
				expandSidebar,
				collapseSidebar,
				sidebarWidth,
				viewMode,
				setViewMode,
				sidebarIconMode,
				setSidebarIconMode,
			}}
		>
			{children}
		</SidebarContext.Provider>
	)
}
